import { Modal } from "shared/components/Modal";
import {
  PropertyGroupSplitBy,
  PropertyGroupStep,
  Segment
} from "views/property-groups-configuration/types";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  IconButton,
  IconButtonSize,
  Input,
  IconButtonVariant
} from "shared/components";
import { useState, useEffect, useMemo } from "react";
import { SegmentsList } from "./SegmentsList";
import { plural } from "pluralize";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Loader } from "shared/components/Loader";
import { ChevronRight } from "untitledui-js-base";
import { Card } from "shared/components/Card";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { getPreviousStep } from "views/property-groups-configuration/utils";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { capitalize } from "shared/utils/capitalize";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

const formatOptionsByType = (
  options: string[],
  type: PropertyGroupSplitBy
): string[] => {
  if (options.length === 0) return [];

  switch (type) {
    case PropertyGroupSplitBy.STATE:
      return options.map((option) => option.toUpperCase());
    case PropertyGroupSplitBy.CITY:
      return options.map((option) =>
        option
          .split(" ")
          .map((word) => capitalize(word))
          .join(" ")
      );
    default:
      return options;
  }
};

export const SplitModal = ({
  opened,
  onClose,
  currentStep,
  selectedSplitOptions,
  splitBy,
  currentSegments,
  updateSegments,
  isLoadingSegments
}: {
  opened: boolean;
  onClose: () => void;
  currentStep: PropertyGroupSplitBy;
  selectedSplitOptions: PropertyGroupSplitBy[];
  splitBy: PropertyGroupSplitBy[];
  currentSegments: any;
  updateSegments: (data: {
    nodeId: string;
    splitDatas: { title: string; splitValues: string[]; isOther: boolean }[];
  }) => void;
  isLoadingSegments: boolean;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const [parentIdentity, setParentIdentity] = useState<string | null>(
    currentStep === selectedSplitOptions[0] ? currentSegments[0].identity : null
  );

  const [searchParentOptions, setSearchParentOptions] = useState("");
  const [segments, setSegments] = useState<Segment[]>([
    {
      name: "",
      options: []
    }
  ]);
  const [othersName, setOthersName] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    segments: {
      name: boolean;
      options: boolean;
      duplicateOptions?: string[];
      duplicateName?: boolean;
    }[];
  }>({
    segments: [{ name: false, options: false }]
  });

  const stepIndex = splitBy.findIndex((step) => step === currentStep);

  const isLastStep =
    currentStep === selectedSplitOptions[selectedSplitOptions.length - 1];

  const segmentAtCurrLevel = useMemo(() => {
    return currentSegments.filter((segment) => {
      const correctLevel = segment.splitDataChain.length === stepIndex + 2;

      if (!parentIdentity || stepIndex === 0) return correctLevel;

      const parentParts = parentIdentity.split("_");

      const segmentIdentity = segment.identity;
      const segmentParts = segmentIdentity.split("_");

      const isDirectChild = segmentParts.length === parentParts.length + 1;

      let isChildOfCurrentParent = true;
      for (let i = 0; i < parentParts.length; i++)
        if (segmentParts[i] !== parentParts[i]) {
          isChildOfCurrentParent = false;
          break;
        }

      return correctLevel && isDirectChild && isChildOfCurrentParent;
    });
  }, [currentSegments, stepIndex, parentIdentity]);

  useEffect(() => {
    if (
      segmentAtCurrLevel.length === 1 &&
      segmentAtCurrLevel[0].title ===
        `Other ${plural(propertyGroupTitleMap[currentStep] || "")}`
    ) {
      setSegments([
        {
          name: "",
          options: []
        }
      ]);
      setOthersName(
        `Other ${plural(propertyGroupTitleMap[currentStep] || "")}`
      );
    } else {
      const data = segmentAtCurrLevel.reduce((acc, item, index) => {
        const splitData = item.splitDataChain[stepIndex + 1] || {};
        const splitValues = splitData.splitValues || [];
        const formattedOptions = formatOptionsByType(splitValues, currentStep);

        if (index === segmentAtCurrLevel.length - 1) {
          setOthersName(item.title);
          setSegments([]);
          return acc;
        }

        acc.push({
          name: item.title,
          options: formattedOptions
        });

        return acc;
      }, []);

      if (data.length > 0) setSegments(data);
    }
  }, [segmentAtCurrLevel, currentStep, stepIndex]);

  const { data: segmentOptions, isLoading: isLoadingSegmentOptions } = useQuery(
    {
      queryKey: ["segments-options", currentStep, parentIdentity],
      queryFn: async () => {
        let extraParams = {};

        if (parentIdentity) {
          const parentSegment = currentSegments.find(
            (segment) => segment.identity === parentIdentity
          );
          const splitDataChain = parentSegment.splitDataChain.map((item) => ({
            split_values: item.splitValues,
            split_type: item.splitType ?? "",
            is_other: item.isOther
          }));
          extraParams = { split_data_chain: splitDataChain };
        }

        const response = await instance.post(
          "/portal/v1/property_groups/segments/options",
          {
            type: currentStep,
            ...extraParams
          }
        );

        return response.data.filter((option) => option.group_value);
      },
      enabled: !!parentIdentity
    }
  );

  const propertyTotalCount = useMemo(
    () =>
      parentIdentity
        ? currentSegments.find((segment) => segment.identity === parentIdentity)
            ?.total_properties || 0
        : currentSegments[0]?.total_properties || 0,
    [parentIdentity, currentSegments]
  );

  useEffect(() => {
    setValidationErrors({
      segments: segments.map(() => ({
        name: false,
        options: false,
        duplicateName: false
      }))
    });
  }, [segments.length]);

  const validateSegments = (): boolean => {
    if (segments.length === 0) {
      setValidationErrors({
        segments: []
      });
      return true;
    }

    const optionCounts: Record<string, number> = {};
    const duplicatesBySegment: string[][] = segments.map(() => []);

    const nameSet = new Set<string>();
    const duplicateNames: boolean[] = segments.map((segment) => {
      const trimmedName = segment.name.trim();
      if (trimmedName === "") return false;

      if (nameSet.has(trimmedName)) return true;

      nameSet.add(trimmedName);
      return false;
    });

    const trimmedOthersName = othersName.trim();
    const isOthersNameDuplicate =
      trimmedOthersName !== "" && nameSet.has(trimmedOthersName);

    segments.forEach((segment) => {
      segment.options.forEach((option) => {
        if (!optionCounts[option]) optionCounts[option] = 1;
        else {
          optionCounts[option]++;
          segments.forEach((checkSegment, checkSegmentIndex) => {
            if (checkSegment.options.includes(option))
              if (!duplicatesBySegment[checkSegmentIndex].includes(option))
                duplicatesBySegment[checkSegmentIndex].push(option);
          });
        }
      });
    });

    const newValidationErrors = {
      segments: [
        ...segments.map((segment, index) => ({
          name: segment.name.trim() === "" || duplicateNames[index],
          options: segment.options.length === 0,
          duplicateOptions:
            duplicatesBySegment[index].length > 0
              ? duplicatesBySegment[index]
              : undefined,
          duplicateName: duplicateNames[index]
        })),
        {
          name: trimmedOthersName === "" || isOthersNameDuplicate,
          options: false,
          duplicateName: isOthersNameDuplicate,
          duplicateOptions: undefined
        }
      ]
    };

    setValidationErrors(newValidationErrors);

    return !newValidationErrors.segments.some(
      (error) => error.name || error.options || error.duplicateOptions
    );
  };

  const handleConfirm = () => {
    if (!validateSegments()) return;

    const selectedOptions = segments.flatMap((segment) => segment.options);

    const splitDatas = [
      ...segments.map((segment) => ({
        title: segment.name,
        splitValues: segment.options,
        isOther: false
      })),
      {
        title: othersName,
        splitValues: selectedOptions,
        isOther: true
      }
    ];
    updateSegments({
      nodeId: parentIdentity || "root",
      splitDatas
    });

    setIsSubmitted(true);
  };

  useEffect(() => {
    if (!isLoadingSegments && isSubmitted) onClose();
  }, [isLoadingSegments, isSubmitted, onClose]);

  const options = currentSegments.filter((segment) => {
    return segment.splitDataChain.length === stepIndex + 1;
  });

  const filteredOptions = useMemo(() => {
    if (!searchParentOptions) return options;
    return options.filter((option) =>
      option.title.toLowerCase().includes(searchParentOptions.toLowerCase())
    );
  }, [options, searchParentOptions]);

  const groupedOptions = useMemo(() => {
    if (stepIndex <= 1) return null;

    const parentLevel = stepIndex - 1;
    const parentSegments = currentSegments.filter(
      (segment) => segment.splitDataChain.length === parentLevel + 1
    );

    const groups = parentSegments.reduce(
      (acc, parentSegment) => {
        const childSegments = options.filter((segment) => {
          const parentPrefix = `${parentSegment.identity}_`;
          const segmentParts = segment.identity.split("_");
          const parentParts = parentSegment.identity.split("_");

          return (
            segment.identity.startsWith(parentPrefix) &&
            segmentParts.length === parentParts.length + 1
          );
        });

        if (childSegments.length > 0)
          acc.push({
            parent: parentSegment,
            children: childSegments
          });

        return acc;
      },
      [] as Array<{ parent: any; children: any[] }>
    );

    const allOtherOptions = options.filter((segment) => {
      return !parentSegments.some((parent) => {
        const parentPrefix = `${parent.identity}_`;
        return segment.identity.startsWith(parentPrefix);
      });
    });

    if (allOtherOptions.length > 0)
      groups.push({
        parent: {
          title: `All other ${plural(propertyGroupTitleMap[getPreviousStep(selectedSplitOptions, currentStep as unknown as PropertyGroupStep)])}`
        },
        children: allOtherOptions
      });

    return groups;
  }, [stepIndex, currentSegments, options, selectedSplitOptions, currentStep]);

  const filteredGroupedOptions = useMemo(() => {
    if (!groupedOptions || !searchParentOptions) return groupedOptions;

    const searchTerm = searchParentOptions.toLowerCase();

    return groupedOptions
      .map((group) => {
        const groupTitleMatches = group.parent.title
          .toLowerCase()
          .includes(searchTerm);

        const matchingChildren = group.children.filter((child) =>
          child.title.toLowerCase().includes(searchTerm)
        );

        if (groupTitleMatches || matchingChildren.length > 0)
          return {
            parent: group.parent,
            children: groupTitleMatches ? group.children : matchingChildren
          };

        return null;
      })
      .filter(Boolean) as Array<{ parent: any; children: any[] }>;
  }, [groupedOptions, searchParentOptions]);

  const clearValidationError = (
    segmentIndex: number,
    fieldType: "name" | "options"
  ) => {
    const hasError =
      fieldType === "name"
        ? validationErrors.segments[segmentIndex]?.name
        : validationErrors.segments[segmentIndex]?.options;

    if (hasError) {
      const updatedErrors = { ...validationErrors };
      if (fieldType === "name")
        updatedErrors.segments[segmentIndex] = {
          ...updatedErrors.segments[segmentIndex],
          name: false,
          duplicateName: false
        };
      else
        updatedErrors.segments[segmentIndex] = {
          ...updatedErrors.segments[segmentIndex],
          options: false
        };
      setValidationErrors(updatedErrors);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-[471px] max-h-[90vh] overflow-hidden flex flex-col"
    >
      {stepIndex > 0 && !parentIdentity ? (
        <>
          <div className="p-4 border-b border-alpha-100 flex-shrink-0">
            <div className="flex justify-between items-center mb-4">
              <div className="text-base font-bold">
                Select{" "}
                <span className="max-w-[150px] truncate">
                  {plural(
                    propertyGroupTitleMap[
                      getPreviousStep(
                        selectedSplitOptions,
                        currentStep as unknown as PropertyGroupStep
                      )
                    ]
                  )}
                </span>{" "}
                to split
              </div>
              <IconButton
                HugeIcon={Cancel01Icon}
                size={IconButtonSize.BASE}
                variant={IconButtonVariant.TEXT_BASE}
                onClick={onClose}
              />
            </div>

            <Input
              id="search"
              className="w-full"
              placeholder="Search"
              value={searchParentOptions}
              onChange={(e) => setSearchParentOptions(e.target.value)}
            />
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background-900 min-h-0">
            {stepIndex > 1 && groupedOptions
              ? filteredGroupedOptions?.map((group, groupIndex) => (
                  <div
                    key={`group-${groupIndex}`}
                    className="space-y-4  pb-6 mb-6 border-b border-alpha-40"
                  >
                    <div className="flex items-center">
                      <div className="h-2 w-2 bg-content-200 rounded-full mr-2"></div>
                      <span className="text-base font-semibold">
                        {group.parent.title}{" "}
                        <span className="max-w-[120px] truncate">
                          {plural(
                            propertyGroupTitleMap[
                              getPreviousStep(
                                selectedSplitOptions,
                                currentStep as unknown as PropertyGroupStep
                              )
                            ]
                          )}
                        </span>
                      </span>
                    </div>

                    {group.children.map((option) => (
                      <Card
                        className="py-4 px-6 flex justify-between items-center"
                        key={option.identity}
                        onClick={() => setParentIdentity(option.identity)}
                      >
                        <div className="space-y-2">
                          <p className="text-base font-bold max-w-[200px] truncate">
                            {option.title}
                          </p>
                          <p className="text-sm text-content-400">{`${option.total_properties} properties`}</p>
                        </div>

                        <ChevronRight className="size-6 stroke-content-200" />
                      </Card>
                    ))}
                  </div>
                ))
              : filteredOptions.map((option) => (
                  <Card
                    className="py-4 px-6 flex justify-between items-center"
                    key={option.identity}
                    onClick={() => setParentIdentity(option.identity)}
                  >
                    <div className="space-y-2">
                      <p className="text-base font-bold max-w-[200px] truncate">
                        {option.title}
                      </p>
                      <p className="text-sm text-content-400">{`${option.total_properties} properties`}</p>
                    </div>

                    <ChevronRight className="size-6 stroke-content-200" />
                  </Card>
                ))}
          </div>
        </>
      ) : isLoadingSegmentOptions ? (
        <div className="flex justify-center items-center flex-1 min-h-[400px]">
          <Loader />
        </div>
      ) : (
        <>
          <div className="p-4 border-b border-alpha-100 space-y-2 flex-shrink-0">
            <div className="text-base font-bold flex items-center">
              <span>Split&nbsp;</span>
              {parentIdentity !== "root" ? (
                <span className="max-w-[150px] truncate">
                  {
                    currentSegments.find(
                      (segment) => segment.identity === parentIdentity
                    )?.title
                  }
                </span>
              ) : (
                `all ${dictionary.properties}`
              )}
              <span>&nbsp;by {propertyGroupTitleMap[currentStep]}</span>
            </div>
            <p className="text-content-400 text-sm">{`${propertyTotalCount} properties`}</p>
          </div>

          <div className="m-4 bg-primary-20 border border-alpha-100 border-dashed rounded-lg p-[30px] mb-6 flex justify-center flex-shrink-0">
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                setSegments([...segments, { name: "", options: [] }]);
              }}
            >
              {isLastStep ? "Add group" : "Add segment"}
            </Button>
          </div>

          <div className="p-4 bg-background-900 border-y border-alpha-100 space-y-6 py-8 overflow-y-auto flex-1 min-h-0">
            <SegmentsList
              currentStep={currentStep}
              segmentOptions={segmentOptions}
              segments={segments}
              setSegments={setSegments}
              propertyTotalCount={propertyTotalCount}
              validationErrors={validationErrors.segments}
              isLastStep={isLastStep}
              clearValidationError={clearValidationError}
              othersName={othersName}
              setOthersName={setOthersName}
            />
          </div>

          <div className="flex justify-end gap-3 py-3 px-4 flex-shrink-0">
            <Button
              variant={ButtonVariant.BASE}
              onClick={() => {
                if (stepIndex > 0 && parentIdentity) {
                  setParentIdentity(null);
                  setSegments([
                    {
                      name: "",
                      options: []
                    }
                  ]);
                } else onClose();
              }}
            >
              {stepIndex > 0 && parentIdentity ? "Back" : "Cancel"}
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={handleConfirm}
              loading={isLoadingSegments}
            >
              Confirm
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};
